import { Observable, OperatorFunction } from 'rxjs';

/**
 * Defers every notification to the next macrotask while keeping the scheduled
 * work owned by the subscription.
 *
 * `observeOn(asyncScheduler)` also defers, but it enqueues the work on a
 * scheduler shared by the whole application. When a consumer is destroyed
 * (`toSignal` does that on component destroy), the action is unsubscribed while
 * a flush may already be queued; the later flush then executes a cancelled
 * action and throws `executing a cancelled action`. This operator schedules its
 * own timer and clears it on teardown, so an abandoned notification is simply
 * dropped instead of reaching a shared queue.
 */
export function deferNotifications<T>(): OperatorFunction<T, T> {
  return (source) =>
    new Observable<T>((subscriber) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      let pending: (() => void)[] = [];

      const flush = () => {
        timer = undefined;
        const tasks = pending;
        pending = [];

        for (const task of tasks) {
          if (subscriber.closed) {
            return;
          }

          task();
        }
      };

      const schedule = (task: () => void) => {
        if (subscriber.closed) {
          return;
        }

        pending.push(task);

        if (timer === undefined) {
          timer = setTimeout(flush, 0);
        }
      };

      const subscription = source.subscribe({
        next: (value) => schedule(() => subscriber.next(value)),
        error: (error) => schedule(() => subscriber.error(error)),
        complete: () => schedule(() => subscriber.complete()),
      });

      return () => {
        pending = [];

        if (timer !== undefined) {
          clearTimeout(timer);
          timer = undefined;
        }

        subscription.unsubscribe();
      };
    });
}
